import { collection, addDoc, GeoPoint, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import firebase from 'firebase/compat/app';
import { geohashForLocation, geohashQueryBounds, distanceBetween } from 'geofire-common';

// Get Firestore instance lazily to avoid initialization errors
const getDb = () => firebase.firestore();

// Configuration for note spawning
const CONFIG = {
  SPAWN_CHANCE: 0.05, // 5% chance per movement check
  MIN_DISTANCE_METERS: 50, // Minimum distance traveled to trigger spawn check
  MIN_DISTANCE_BETWEEN_NOTES: 100, // Minimum distance between notes
  MAX_NOTES_PER_AREA: 5, // Max special notes within 500m radius
  AREA_CHECK_RADIUS: 500, // Radius to check for existing notes
};

// Note tier configuration
const NOTE_TIERS = {
  bronze: {
    name: 'Bronze Note',
    pointValue: 10,
    spawnWeight: 50, // Higher = more common
    color: '#CD7F32',
    emoji: '🥉',
  },
  silver: {
    name: 'Silver Note',
    pointValue: 25,
    spawnWeight: 30,
    color: '#C0C0C0',
    emoji: '🥈',
  },
  gold: {
    name: 'Gold Note',
    pointValue: 100,
    spawnWeight: 15,
    color: '#FFD700',
    emoji: '🥇',
  },
  platinum: {
    name: 'Platinum Note',
    pointValue: 250,
    spawnWeight: 5,
    color: '#E5E4E2',
    emoji: '💎',
  },
};

class GoldenNoteService {
  constructor() {
    this.lastSpawnCheckLocation = null;
    this.lastCheckTime = null;
  }

  /**
   * Select a random tier based on spawn weights
   * @returns {string} - Tier key (bronze, silver, gold, platinum)
   */
  selectRandomTier() {
    const totalWeight = Object.values(NOTE_TIERS).reduce((sum, tier) => sum + tier.spawnWeight, 0);
    let random = Math.random() * totalWeight;

    for (const [tier, config] of Object.entries(NOTE_TIERS)) {
      random -= config.spawnWeight;
      if (random <= 0) {
        return tier;
      }
    }

    return 'bronze'; // Fallback
  }

  /**
   * Check if we should spawn a golden note based on user movement
   * @param {Object} currentLocation - { lat, lng }
   * @param {string} userId - Current user's ID
   * @returns {Promise<boolean>} - Whether a golden note was spawned
   */
  async checkAndSpawnGoldenNote(currentLocation, userId) {
    try {
      // Don't check too frequently (min 30 seconds between checks)
      const now = Date.now();
      if (this.lastCheckTime && (now - this.lastCheckTime) < 30000) {
        return false;
      }

      // Check if user has moved enough distance
      if (this.lastSpawnCheckLocation) {
        const distance = distanceBetween(
          [this.lastSpawnCheckLocation.lat, this.lastSpawnCheckLocation.lng],
          [currentLocation.lat, currentLocation.lng]
        );

        if (distance < CONFIG.MIN_DISTANCE_METERS) {
          return false; // Not moved enough
        }
      }

      // Update last check
      this.lastSpawnCheckLocation = currentLocation;
      this.lastCheckTime = now;

      // Random chance to spawn
      if (Math.random() > CONFIG.SPAWN_CHANCE) {
        return false; // Didn't hit the spawn chance
      }

      // Check if there are already too many special notes in the area
      const nearbySpecialNotes = await this.getSpecialNotesInArea(
        currentLocation,
        CONFIG.AREA_CHECK_RADIUS
      );

      if (nearbySpecialNotes.length >= CONFIG.MAX_NOTES_PER_AREA) {
        console.log('Too many special notes in area, skipping spawn');
        return false;
      }

      // Select random tier
      const tier = this.selectRandomTier();

      // Generate a random spawn location within a small radius
      const spawnLocation = this.generateRandomNearbyLocation(currentLocation, 20, 40);

      // Spawn the tiered note
      await this.spawnTieredNote(spawnLocation, tier, userId);
      const tierConfig = NOTE_TIERS[tier];
      console.log(`${tierConfig.emoji} ${tierConfig.name} spawned at`, spawnLocation);
      return true;

    } catch (error) {
      console.error('Error in checkAndSpawnGoldenNote:', error);
      return false;
    }
  }

  /**
   * Get all special notes (tiered, collectible) in a specific area
   * @param {Object} center - { lat, lng }
   * @param {number} radiusInM - Radius in meters
   * @returns {Promise<Array>} - Array of special note documents
   */
  async getSpecialNotesInArea(center, radiusInM) {
    const db = getDb();
    const notesRef = collection(db, 'tokens');
    const bounds = geohashQueryBounds([center.lat, center.lng], radiusInM);
    const specialNotes = [];

    for (const b of bounds) {
      const q = query(
        notesRef,
        where('location.geohash', '>=', b[0]),
        where('location.geohash', '<=', b[1]),
        where('active', '==', true)
      );

      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Check if it's a tiered note or collectible
        if (data.noteTier || data.noteType === 'collectible') {
          const noteLocation = data.location.geopoint;
          const distance = distanceBetween(
            [center.lat, center.lng],
            [noteLocation.latitude, noteLocation.longitude]
          );

          if (distance <= radiusInM) {
            specialNotes.push({ id: doc.id, ...data });
          }
        }
      });
    }

    return specialNotes;
  }

  /**
   * Generate a random location nearby
   * @param {Object} center - { lat, lng }
   * @param {number} minRadiusM - Minimum radius in meters
   * @param {number} maxRadiusM - Maximum radius in meters
   * @returns {Object} - { lat, lng }
   */
  generateRandomNearbyLocation(center, minRadiusM, maxRadiusM) {
    // Convert meters to approximate degrees (rough approximation)
    const metersPerDegree = 111320;
    const minRadius = minRadiusM / metersPerDegree;
    const maxRadius = maxRadiusM / metersPerDegree;

    const radius = minRadius + Math.random() * (maxRadius - minRadius);
    const angle = Math.random() * 2 * Math.PI;

    const deltaLat = radius * Math.cos(angle);
    const deltaLng = radius * Math.sin(angle) / Math.cos(center.lat * Math.PI / 180);

    return {
      lat: center.lat + deltaLat,
      lng: center.lng + deltaLng,
    };
  }

  /**
   * Spawn a tiered note at a specific location
   * @param {Object} location - { lat, lng }
   * @param {string} tier - Tier key (bronze, silver, gold, platinum)
   * @param {string} spawnedByUserId - ID of user whose movement triggered the spawn
   */
  async spawnTieredNote(location, tier, spawnedByUserId) {
    const db = getDb();
    const tierConfig = NOTE_TIERS[tier];

    const tokenData = {
      title: `${tierConfig.emoji} ${tierConfig.name}`,
      description: `A ${tier} note worth ${tierConfig.pointValue} points! Pick it up to add it to your collection.`,
      active: true,
      expiresAt: null, // Tiered notes don't expire
      batchId: null,
      location: {
        geopoint: new GeoPoint(location.lat, location.lng),
        geohash: geohashForLocation([location.lat, location.lng]),
        address: '',
        placeName: '',
      },
      createdBy: 'system', // System-generated
      createdAt: serverTimestamp(),
      currentOwner: null, // No owner until picked up
      currentHolder: import.meta.env.VITE_EARTH_MOTHER || 'earth_mother',
      lastTransferTime: serverTimestamp(),
      tokenType: 'note',
      noteType: 'collectible', // Tiered notes are collectible
      noteTier: tier,
      updatedAt: serverTimestamp(),
      isPublic: true,
      tags: [tier, 'tiered', 'collectible'],
      pointValue: tierConfig.pointValue,
      canBePickedUp: true,
      pickupCount: 0,
      spawnedBy: spawnedByUserId,
      spawnedAt: serverTimestamp(),
    };

    const tokensCollection = collection(db, 'tokens');
    await addDoc(tokensCollection, tokenData);
  }

  /**
   * Reset the spawn check (useful when user logs in/out)
   */
  reset() {
    this.lastSpawnCheckLocation = null;
    this.lastCheckTime = null;
  }
}

// Export a singleton instance
export default new GoldenNoteService();
